import { FIELD_LABELS, fieldLabel } from "./mergeFields";

const FIELD_RE = /\{\{\s*(\w+)\s*\}\}/g;
const CHIP_STYLE = "display:inline-block;background:#e0f2fe;color:#075985;border:1px solid #bae6fd;border-radius:9999px;padding:0 7px;margin:0 1px;font-size:12px;font-weight:600;line-height:18px;white-space:nowrap;user-select:all;";

// Tags the visual view and the sender keep (mirrors the backend sanitizer).
const SUPPORTED = new Set(["h1", "h2", "h3", "h4", "p", "br", "hr", "strong", "b", "em", "i", "u", "ul", "ol", "li", "a",
  "table", "thead", "tbody", "tr", "td", "th", "img", "span", "div", "blockquote", "font", "s", "sub", "sup", "small", "center", "pre", "code"]);

const DROP = ["script", "style", "iframe", "object", "embed", "head", "meta", "link", "title", "xml", "svg", "form", "input", "button"];
const KEEP_ATTR = { a: ["href"], img: ["src", "alt", "width", "height"], td: ["colspan", "rowspan"], th: ["colspan", "rowspan"] };
const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Pasted HTML (Outlook, Word, web, ChatGPT) -> clean structural HTML: keeps paragraphs, breaks, bold/italic/underline,
// lists, links, tables and https images; drops styles, classes, Office markup and anything unsafe.
export function cleanPastedHtml(html) {
  const m = html.match(/<!--StartFragment-->([\s\S]*?)<!--EndFragment-->/);
  const frag = parse(m ? m[1] : html);
  const walker = document.createTreeWalker(frag, NodeFilter.SHOW_COMMENT);
  const comments = []; while (walker.nextNode()) comments.push(walker.currentNode);
  comments.forEach((c) => c.remove());
  [...frag.querySelectorAll("*")].reverse().forEach((el) => {
    const t = el.tagName.toLowerCase();
    if (DROP.includes(t)) { el.remove(); return; }
    const st = (el.getAttribute("style") || "").toLowerCase();
    if (t === "b" && /font-weight:\s*(normal|400)/.test(st)) { el.replaceWith(...el.childNodes); return; }
    if (t === "span" && (/font-weight:\s*(bold|[6-9]00)/.test(st) || /font-style:\s*italic/.test(st))) {
      let inner = [...el.childNodes];
      if (/font-style:\s*italic/.test(st)) { const i = document.createElement("em"); i.append(...inner); inner = [i]; }
      if (/font-weight:\s*(bold|[6-9]00)/.test(st)) { const b = document.createElement("strong"); b.append(...inner); inner = [b]; }
      el.replaceWith(...inner); return;
    }
    if (!SUPPORTED.has(t) || t === "span" || t === "font") { el.replaceWith(...el.childNodes); return; }
    if (t === "img" && !/^https:\/\//i.test(el.getAttribute("src") || "")) { el.remove(); return; }
    [...el.attributes].forEach((a) => { if (!(KEEP_ATTR[t] || []).includes(a.name)) el.removeAttribute(a.name); });
    if (t === "a") {
      const h = el.getAttribute("href") || "";
      if (!/^(https?:|mailto:|tel:)/i.test(h)) el.removeAttribute("href");
    }
  });
  frag.querySelectorAll("p, div").forEach((el) => { if (!el.textContent.replace(/\u00a0/g, " ").trim() && !el.querySelector("img, br, table")) el.remove(); });
  return serialize(frag).trim();
}

// Plain text -> paragraphs (blank line) and line breaks (single newline). Nothing else is invented.
export function textToHtml(text) {
  return (text || "").replace(/\r\n?/g, "\n").split(/\n{2,}/).filter((p) => p.trim())
    .map((p) => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`).join("");
}

export const isBlankHtml = (html) => !parse(html).textContent.replace(/[\u00a0\u200B]/g, " ").trim() && !/<(img|table)/i.test(html || "");

const parse = (html) => { const t = document.createElement("template"); t.innerHTML = html || ""; return t.content; };
const serialize = (frag) => { const d = document.createElement("div"); d.appendChild(frag); return d.innerHTML; };

export function chipHtml(key) {
  return `<span data-merge="${key}" contenteditable="false" style="${CHIP_STYLE}">${fieldLabel(key)}</span>`;
}

const INLINE = new Set(["#text", "BR", "STRONG", "B", "EM", "I", "U", "A", "SPAN", "FONT", "S", "SUB", "SUP", "SMALL", "IMG"]);

// Loose text separated by <br><br> becomes real paragraphs, so Enter/lists behave predictably.
function paragraphize(frag) {
  const kids = [...frag.childNodes];
  if (!kids.some((n) => n.nodeName === "BR")) return;
  let run = [];
  const blank = (x) => x.nodeType === 3 && !x.nodeValue.trim();
  const flush = (before) => {
    const segs = [[]];
    for (let i = 0; i < run.length; i++) {
      const n = run[i];
      if (n.nodeName === "BR") {
        let j = i + 1;
        while (j < run.length && blank(run[j])) j++;
        if (run[j]?.nodeName === "BR") {
          for (let k = i; k <= j; k++) run[k].remove();
          segs.push([]); i = j; continue;
        }
      }
      segs[segs.length - 1].push(n);
    }
    segs.forEach((seg) => {
      while (seg.length && (blank(seg[seg.length - 1]) || seg[seg.length - 1].nodeName === "BR")) seg.pop().remove();
      const meaningful = seg.filter((n) => !blank(n));
      if (!meaningful.length) { seg.forEach((n) => n.remove()); return; }
      const p = document.createElement("p");
      frag.insertBefore(p, before);
      seg.forEach((n) => p.appendChild(n));
      if (p.firstChild?.nodeType === 3) p.firstChild.nodeValue = p.firstChild.nodeValue.replace(/^\s+/, "");
      if (p.lastChild?.nodeType === 3) p.lastChild.nodeValue = p.lastChild.nodeValue.replace(/\s+$/, "");
    });
    run = [];
  };
  kids.forEach((n) => { if (INLINE.has(n.nodeName)) run.push(n); else flush(n); });
  flush(null);
}

// Canonical HTML ({{key}}) -> editor HTML (friendly chips). Only text nodes are touched; attributes keep {{key}}.
export function toVisual(html) {
  const frag = parse(html);
  paragraphize(frag);
  frag.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach((a) => { if (/^on/i.test(a.name)) el.removeAttribute(a.name); });
  });
  const walker = document.createTreeWalker(frag, NodeFilter.SHOW_TEXT);
  const texts = [];
  while (walker.nextNode()) {
    const n = walker.currentNode;
    if (FIELD_RE.test(n.nodeValue) && !n.parentElement?.closest?.("[data-merge]")) texts.push(n);
    FIELD_RE.lastIndex = 0;
  }
  texts.forEach((n) => {
    const holder = document.createElement("span");
    holder.innerHTML = n.nodeValue.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(FIELD_RE, (_, k) => chipHtml(k));
    n.replaceWith(...holder.childNodes);
  });
  return serialize(frag);
}

// Editor DOM -> canonical HTML.
export function fromVisual(root) {
  const clone = root.cloneNode(true);
  clone.querySelectorAll("[data-merge]").forEach((c) => c.replaceWith(document.createTextNode(`{{${c.getAttribute("data-merge")}}}`)));
  // Drop editor-only styling the browser copies in (the editor's 14px base font), and unwrap now-bare spans.
  clone.querySelectorAll("[style]").forEach((el) => {
    if (el.style.fontSize === "0.875rem") el.style.removeProperty("font-size");
    if (!el.getAttribute("style").trim()) el.removeAttribute("style");
  });
  clone.querySelectorAll("span:not([class]):not([style])").forEach((s) => s.replaceWith(...s.childNodes));
  // A paragraph can't contain blocks (browser editing can nest them): unwrap it, keeping order.
  clone.querySelectorAll("p").forEach((p) => {
    if (p.querySelector(":scope > ul, :scope > ol, :scope > p, :scope > div, :scope > h1, :scope > h2, :scope > h3, :scope > table")) p.replaceWith(...p.childNodes);
  });
  return clone.innerHTML.replace(/\u200B/g, "");
}

export function unsupportedTags(html) {
  const found = new Set();
  parse(html).querySelectorAll("*").forEach((el) => { const t = el.tagName.toLowerCase(); if (!SUPPORTED.has(t)) found.add(t); });
  return [...found];
}

export function stripUnsupported(html) {
  const frag = parse(html);
  frag.querySelectorAll("*").forEach((el) => {
    const t = el.tagName.toLowerCase();
    if (SUPPORTED.has(t)) return;
    if (["script", "style", "iframe", "object", "embed", "head", "meta", "link", "title"].includes(t)) el.remove();
    else el.replaceWith(...el.childNodes);
  });
  return serialize(frag);
}

// Client-side sample preview (templates): same single-signature rule as the sender.
export function samplePreview(html, sample) {
  if (!html) return "<em style='color:#94a3b8'>No content</em>";
  const sig = (sample.sender_signature || "").trim();
  let body = html;
  const hasSig = /\{\{\s*sender_signature\s*\}\}/.test(body);
  if (hasSig) { const parts = body.split(/\{\{\s*sender_signature\s*\}\}/); body = parts[0] + "{{sender_signature}}" + parts.slice(1).join(""); }
  body = body.replace(FIELD_RE, (m, key) => (key in sample && String(sample[key]).trim()) ? sample[key]
    : `<span style="background:#fef3c7;color:#92400e;border-radius:3px;padding:0 4px;">${FIELD_LABELS[key] || key}</span>`);
  if (sig && !hasSig) body += `<br><br>${sig}`;
  return body;
}
